import { FormatType } from '@revelio/replots';

import { MAX_REPORT_COMPETITORS } from '../components/PageConfigProvider/constants';
import {
  PageType,
  type ReportPageConfig,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';
import {
  buildFilterGroups,
  encodeFilterGroups,
} from '../filters/utils/filterGroupsUtils';

/** Shared bind fields for Snowflake sentiment peer-comparison breakdowns (MSA + role). */
const sentimentPeerComparisonSnowflakeFields = [
  {
    name: 'rcid',
    label: 'Company',
    value: '',
    fieldType: 'number' as const,
    required: true,
    parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
  },
  {
    name: 'competitor_rcids',
    label: 'Competitors',
    value: '',
    fieldType: 'number[]' as const,
    required: false,
    parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
  },
  {
    name: 'top_n',
    label: 'Top N',
    value: '20',
    fieldType: 'number' as const,
    required: false,
  },
  {
    name: 'custom_tax_id',
    label: 'Role taxonomy',
    value: 'default',
    fieldType: 'string' as const,
    required: true,
  },
];

export const reportCompanySentiment: ReportPageConfig = {
  id: 'report-company-sentiment',
  name: 'Sentiment',
  type: PageType.REPORT,
  owner: {
    id: 'db176d03-a841-44ee-bedb-78b2aa87c32a',
    username: 'jeremie@reveliolabs.com',
  },
  sampleFilterGroups: encodeFilterGroups(
    buildFilterGroups(
      [
        {
          groupKey: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
          filterIds: [FilterId.COMPANY],
          defaultSelections: {
            [FilterId.COMPANY]: [
              {
                id: '6_302_806872',
                name: 'FedEx Corp.',
                levelId: 'company',
                isCurrent: true,
              },
            ],
          },
        },
        {
          groupKey: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
          filterIds: [FilterId.COMPANY],
          defaultSelections: {
            [FilterId.COMPANY]: [
              {
                id: '6_302_1159065',
                name: 'Kintetsu World Express, Inc.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '0_0_204',
                name: 'TForce Logistics, Inc.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '6_302_22142761',
                name: 'United Parcel Service, Inc.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '6_302_254822',
                name: 'Ryder System, Inc.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '6_302_923636',
                name: 'LaserShip, Inc.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '0_0_5060',
                name: 'Burris Logistics, Inc.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '6_300_210583',
                name: 'OnTrac',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '0_0_200359',
                name: 'China Postal Express & Logistics Co., Ltd.',
                levelId: 'company',
                isCurrent: true,
              },
              {
                id: '6_302_642206',
                name: 'ArcBest Corp.',
                levelId: 'company',
                isCurrent: true,
              },
            ],
          },
        },
      ],
      { primaryFilterId: FilterId.COMPANY }
    )
  ),
  tabs: [
    {
      label: 'Company-level employee sentiment',
      id: 'default',
      isActive: true,
      tabContent: {
        id: 'company-sentiment',
        title: 'Sentiment',
        description:
          'Understanding employee sentiment within an organization plays a critical role in analyzing company culture and overall workforce satisfaction, offering valuable insights for talent management, employee engagement strategies, productivity improvements, and retention efforts. Monitoring sentiment trends helps to identify areas of concern, gauge the effectiveness of leadership, and foster a positive work environment, ultimately contributing to better business outcomes and a more resilient workforce.',
        grid: {
          rows: 504,
          columns: 48,
        },
        items: [
          {
            id: 'about-text',
            title: '',
            position: {
              x: 0,
              y: 38,
              width: 48,
              height: 32,
            },
            plot: {
              chartType: 'ai-summary',
              textHtml: '## About\nDescription',
            },
          },
          {
            id: 'a278a325-b9f5-47d3-a150-d755089b2a29',
            title: '',
            position: {
              x: 0,
              y: 74,
              width: 27,
              height: 21,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '## Industry overview\nComparing a company\'s employee sentiment to its peers requires careful consideration of comparable organizations. We select these peer companies using our competitors model, which accounts for factors such as industry and company size.\n\nHere, we show an overview of the peer group selected. We select the 10 closest companies to  {{PARAM "Company"}} from our competitors model and weight them relative to their current employee headcounts.\n\nWe observe around {{FORMAT (SUM "headcount" timeAggregation="latest") format="SI"}} people currently employed at one of these peer companies. In the peer group, {{ARG_MAX "headcount"}} has the largest number of employees, representing {{FORMAT (SHARE "headcount" entity=(ARG_MAX "headcount")) format="PERCENTAGE"}} of the total headcount.',
              plotParameters: [
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: 'cf57de6c-b05b-4465-8627-133f6b1179bf',
            title: '',
            position: {
              x: 0,
              y: 110,
              width: 48,
              height: 12,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '## Overall sentiment\n\nOverall sentiment ratings among employees reflect their collective feelings and attitudes toward the workplace, indicating levels of job satisfaction, engagement, and organizational culture; higher ratings generally signify a positive work environment, while lower ratings may highlight areas needing improvement or concern. To understand where {{PARAM "Company"}} stands relative to its peers, we examine employees\' overall sentiment ratings across its top 10 competitors.\n\n{{PARAM "Company"}} currently ranks {{FORMAT (RANK "overall" (PARAM "Company")) format="ORDINAL"}} in overall employee ratings among its peer companies. Its overall sentiment rating peaked at {{FORMAT (MAX_POINT "overall" (PARAM "Company") property="value") format="PERCENTAGE"}} in {{FORMAT (MAX_POINT "overall"  (PARAM "Company") property="date") format="DATE"}}. It currently stands at {{FORMAT (VALUE "overall" entity=(PARAM "Company") timeAggregation="latest") format="PERCENTAGE"}}, which is {{FORMAT (subtract (VALUE "overall" entity=(PARAM "Company") timeAggregation="latest") (AVG "overall" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} {{#if (gt (VALUE "overall" entity=(PARAM "Company") timeAggregation="latest") (AVG "overall" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}higher{{else}}lower{{/if}} than the average among its peers.',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '18c71108-f0bc-47cd-8bcb-5ffb18ed9f4a',
            title: 'Overall sentiment among peers',
            position: {
              x: 0,
              y: 122,
              width: 23,
              height: 12,
            },
            plot: {
              chartType: 'bar',
              plotFilters: [],
              defaultMetricOptions: ['overall'],
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '2d2cb7c9-7b56-493e-b5f2-9feac484aec6',
            title: 'Overall Sentiment over time',
            position: {
              x: 23,
              y: 122,
              width: 25,
              height: 10,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['overall'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '7752d529-e344-4b6a-86bf-102f9bd93950',
            title: '',
            position: {
              x: 0,
              y: 146,
              width: 48,
              height: 17,
            },
            plot: {
              chartType: 'text',
              textHtml:
                "## Key areas of focus\nOverall sentiment ratings provide a high-level snapshot of general feelings, but they may not fully capture the nuances of employees' experiences across different areas. Perspectives often vary based on specific aspects of their work environment.\n\nNext, we delve into a detailed breakdown across three key areas:\n\n* Workplace experience: This covers aspects such as work-life balance, company culture and values, and initiatives supporting diversity and inclusion.\n* Growth and compensation: This encompasses factors such as pay and benefits, career development opportunities, and perceptions of the organization's future outlook.\n* Leadership: This focuses on employees' views of management and leadership at all levels, including senior leadership and the CEO.\n\nWe then analyze sentiment across different roles and locations. Additionally, we leverage raw text reviews to identify key topics and recurring themes, providing deeper insights into employees' experiences.",
            },
          },
          {
            id: 'a3751d61-cfad-452f-b91e-17a86639032b',
            title: '',
            position: {
              x: 0,
              y: 182,
              width: 48,
              height: 11,
            },
            plot: {
              chartType: 'text',
              textHtml: `## Workplace experience\n\nWorkplace experience includes factors such as work-life balance, company culture and values, and efforts toward diversity and inclusion. It assesses how supported and engaged employees feel in their roles. Here, we show the differences in sentiment ratings between {{PARAM "Company"}} and peer companies along these three dimensions.\n\nFor Culture & Values, the {{#if (gt (subtract (VALUE "culture" entity=(PARAM "Company") timeAggregation="latest") (AVG "culture" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "culture" entity=(PARAM "Company") timeAggregation="latest") (AVG "culture" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} may indicate {{#if (gt (subtract (VALUE "culture" entity=(PARAM "Company") timeAggregation="latest") (AVG "culture" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}strong{{else}}a need for stronger{{/if}} alignment with core organizational values and culture. For Diversity & Inclusion, the {{#if (gt (subtract (VALUE "diversity_and_inclusion" entity=(PARAM "Company") timeAggregation="latest") (AVG "diversity_and_inclusion" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "diversity_and_inclusion" entity=(PARAM "Company") timeAggregation="latest") (AVG "diversity_and_inclusion" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} suggests that employees feel {{#if (gt (subtract (VALUE "diversity_and_inclusion" entity=(PARAM "Company") timeAggregation="latest") (AVG "diversity_and_inclusion" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}relatively more{{else}}less{{/if}} supported in an inclusive environment compared to peers. In Work Life Balance, the {{#if (gt (subtract (VALUE "work_life_balance" entity=(PARAM "Company") timeAggregation="latest") (AVG "work_life_balance" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "work_life_balance" entity=(PARAM "Company") timeAggregation="latest") (AVG "work_life_balance" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} indicates that employees experience a {{#if (gt (subtract (VALUE "work_life_balance" entity=(PARAM "Company") timeAggregation="latest") (AVG "work_life_balance" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}supportive{{else}}challenging{{/if}} balance between work and personal life.`,
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '2cdc459f-8200-456c-9290-f048f2d8aa63',
            title: 'Work-Life Balance',
            position: {
              x: 0,
              y: 193,
              width: 16,
              height: 9,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['work_life_balance'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin:
                "{{MIN (MIN 'work_life_balance') (MIN 'culture') (MIN 'diversity_and_inclusion')}}",
              yAxisMax:
                "{{MAX (MAX 'work_life_balance') (MAX 'culture') (MAX 'diversity_and_inclusion')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '452804d5-050b-45a9-95c9-952b85a21777',
            title: 'Diversity & Inclusion',
            position: {
              x: 32,
              y: 193,
              width: 16,
              height: 9,
            },
            plot: {
              chartType: 'line',
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              defaultMetricOptions: ['diversity_and_inclusion'],
              yAxisMin:
                "{{MIN (MIN 'work_life_balance') (MIN 'culture') (MIN 'diversity_and_inclusion')}}",
              yAxisMax:
                "{{MAX (MAX 'work_life_balance') (MAX 'culture') (MAX 'diversity_and_inclusion')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '1a22b42c-c62c-424c-892d-16571eacfc3b',
            title: 'Culture & Values',
            position: {
              x: 16,
              y: 193,
              width: 16,
              height: 9,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['culture'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin:
                "{{MIN (MIN 'work_life_balance') (MIN 'culture') (MIN 'diversity_and_inclusion')}}",
              yAxisMax:
                "{{MAX (MAX 'work_life_balance') (MAX 'culture') (MAX 'diversity_and_inclusion')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '9c04e167-3bc2-4d1c-978b-fd044ffdee48',
            title: null,
            position: {
              x: 0,
              y: 290,
              width: 48,
              height: 12,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '## Breakdown by roles\n\nAnalyzing employee sentiment by role shows how different positions are perceived within {{PARAM "Company"}} versus the comparison group—where satisfaction is strong and where engagement or retention may lag.\n\nThese views use Glassdoor-linked reviews in a rolling recent window (see Snowflake saved query). Role labels come from the Revelio role taxonomy; this report uses the default taxonomy (other taxonomies can be selected when editing the chart). We rank the top 20 roles by review volume across the company and the comparison group and include a role when either side has at least two reviews in that segment.\n\nThe chart below compares overall sentiment for {{PARAM "Company"}} and the comparison group across those roles.',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '7153e84c-75c4-448c-b113-fa946674dd18',
            title: '',
            position: {
              x: 23,
              y: 132,
              width: 25,
              height: 10,
            },
            plot: {
              chartType: 'gauge',
              colorPalette: ['#FF646D', '#3DC679'],
              defaultMetricOptions: ['overall'],
              gaugeSubText: 'Overall employee sentiment',
              gaugePrimaryText:
                '{{FORMAT (PERCENTILE_RANK "overall" (PARAM "Company")) format="%"}}',
              gaugeValue: '{{PERCENTILE_RANK "overall" (PARAM "Company")}}',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '86a68604-95c6-4f11-b538-95536685468a',
            title: '',
            position: {
              x: 0,
              y: 134,
              width: 23,
              height: 8,
            },
            plot: {
              chartType: 'text',
              textHtml:
                "We calculate a company's overall satisfaction score by comparing its employees' overall sentiment ratings with those of its peers. The ratings are normalized to a 0 to 1 scale. A higher score indicates that employees are generally satisfied with their employer, while a lower score suggests dissatisfaction.",
            },
          },
          {
            id: '6e0bfec1-459c-4b6a-a705-6901c7e98ac5',
            title: '',
            position: {
              x: 0,
              y: 202,
              width: 25,
              height: 10,
            },
            plot: {
              chartType: 'text',
              textHtml:
                'We calculate the workplace experience score of a company by averaging the scores for current ratings across factors such as work-life balance, company culture and values, and efforts toward diversity and inclusion, comparing the company with its peers. These scores are then normalized to a 0 to 1 scale. A higher score indicates that employees are generally satisfied with their workplace experience, while a lower score suggests dissatisfaction.',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: 'c4095abc-0d75-4eb7-a15a-db36b83fcac4',
            title: '',
            position: {
              x: 25,
              y: 202,
              width: 23,
              height: 10,
            },
            plot: {
              chartType: 'gauge',
              colorPalette: ['#FF646D', '#3DC679'],
              defaultMetricOptions: ['work_life_balance'],
              gaugeSubText: 'Workplace experience',
              gaugePrimaryText:
                '{{FORMAT (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) format="%"}}',
              gaugeValue:
                '{{PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")}}',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '6d0515ba-4c5a-497e-8863-cf12b06d8acc',
            title:
              'Overall sentiment by role (top 20): {{PARAM "Company"}} vs comparison group',
            position: {
              x: 0,
              y: 302,
              width: 48,
              height: 18,
            },
            plot: {
              chartType: 'diverging-bar',
              dataSource: 'snowflake',
              plotFilters: [],
              defaultMetricOptions: ['overall'],
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
              showDataLabels: true,
              divergingBar: {
                arrowOrientation: 'vertical',
                positiveLabel: 'Higher Satisfaction',
                negativeLabel: 'Lower Satisfaction',
              },
              comparisonTransformation: {
                baseline: 'second',
              },
              snowflakeConfig: {
                savedQueryId: 'sentiment-role-breakdown-v1',
                fields: sentimentPeerComparisonSnowflakeFields,
              },
            },
          },
          {
            id: '3d11ebf8-5e88-4d42-b43a-be26e9906011',
            title: '',
            position: {
              x: 0,
              y: 218,
              width: 48,
              height: 11,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '## Growth and compensation\n\nGrowth and compensation assesses employees\' perceptions of compensation and benefits, their confidence in the company\'s overall business outlook. Here, we show the differences in sentiment ratings between {{PARAM "Company"}} and peer companies along these three dimensions.\n\nFor Business Outlook, the {{#if (gt (subtract (VALUE "business_outlook" entity=(PARAM "Company") timeAggregation="latest") (AVG "business_outlook" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "business_outlook" entity=(PARAM "Company") timeAggregation="latest") (AVG "business_outlook" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} suggests employees may have {{#if (gt (subtract (VALUE "business_outlook" entity=(PARAM "Company") timeAggregation="latest") (AVG "business_outlook" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}confidence{{else}}concerns{{/if}} about the company\'s future direction or stability. For Career Opportunities, the {{#if (gt (subtract (VALUE "career_growth" entity=(PARAM "Company") timeAggregation="latest") (AVG "career_growth" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "career_growth" entity=(PARAM "Company") timeAggregation="latest") (AVG "career_growth" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} indicates employees may perceive {{#if (gt (subtract (VALUE "career_growth" entity=(PARAM "Company") timeAggregation="latest") (AVG "career_growth" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}strong{{else}}limited{{/if}} growth or advancement opportunities. For Compensation & Benefits, the {{#if (gt (subtract (VALUE "compensation" entity=(PARAM "Company") timeAggregation="latest") (AVG "compensation" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "compensation" entity=(PARAM "Company") timeAggregation="latest") (AVG "compensation" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} suggests employees may feel {{#if (gt (subtract (VALUE "compensation" entity=(PARAM "Company") timeAggregation="latest") (AVG "compensation" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}valued{{else}}undervalued{{/if}} in terms of pay or benefits compared to their expectations.',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '40dff038-d042-4255-a945-6903f793ac28',
            title: 'Compensation & Benefits',
            position: {
              x: 0,
              y: 229,
              width: 16,
              height: 8,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['compensation'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin:
                "{{MIN (MIN 'career_growth') (MIN 'compensation') (MIN 'business_outlook')}}",
              yAxisMax:
                "{{MAX (MAX 'career_growth') (MAX 'compensation') (MAX 'business_outlook')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '88640702-f421-4460-976a-315a1419fa27',
            title: 'Career Growth',
            position: {
              x: 16,
              y: 229,
              width: 16,
              height: 8,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['career_growth'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin:
                "{{MIN (MIN 'career_growth') (MIN 'compensation') (MIN 'business_outlook')}}",
              yAxisMax:
                "{{MAX (MAX 'career_growth') (MAX 'compensation') (MAX 'business_outlook')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '3acc49f2-8414-4114-ade8-b9491e4a506e',
            title: 'Business Outlook',
            position: {
              x: 32,
              y: 229,
              width: 16,
              height: 8,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['business_outlook'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin:
                "{{MIN (MIN 'career_growth') (MIN 'compensation') (MIN 'business_outlook')}}",
              yAxisMax:
                "{{MAX (MAX 'career_growth') (MAX 'compensation') (MAX 'business_outlook')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '3cdf284d-00a0-410d-b243-205d8a7a76bc',
            title: '',
            position: {
              x: 0,
              y: 237,
              width: 25,
              height: 10,
            },
            plot: {
              chartType: 'text',
              textHtml:
                'We calculate the growth and compensation score of a company by averaging the scores for current ratings across factors such as compensation & benefits, career opportunities, and business outlook, comparing the company with its peers. These scores are then normalized to a 0 to 1 scale. A higher score indicates that employees are generally satisfied with their growth and compensation, while a lower score suggests dissatisfaction.',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: 'cab6affc-6558-423a-8259-6a746b9e21f6',
            title: '',
            position: {
              x: 25,
              y: 237,
              width: 23,
              height: 10,
            },
            plot: {
              chartType: 'gauge',
              colorPalette: ['#FF646D', '#3DC679'],
              defaultMetricOptions: ['career_growth'],
              gaugeSubText: 'Growth and compensation',
              gaugePrimaryText:
                '{{FORMAT (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) format="%"}}',
              gaugeValue:
                '{{PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")}}',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: 'eb968509-e3e7-4e81-b352-3563df8c003e',
            title: '',
            position: {
              x: 0,
              y: 254,
              width: 48,
              height: 10,
            },
            plot: {
              chartType: 'text',
              textHtml: `## Leadership\n\nLeadership focuses on employees' views of management and leadership at all levels, including senior leadership and the CEO. It evaluates trust and the effectiveness of communication within leadership. Here, we show the differences in sentiment ratings between {{PARAM "Company"}} and peer companies along these two dimensions.\n\nFor ratings on Leadership, the {{#if (gt (subtract (VALUE "leadership" entity=(PARAM "Company") timeAggregation="latest") (AVG "leadership" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "leadership" entity=(PARAM "Company") timeAggregation="latest") (AVG "leadership" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} indicates employees may {{#if (gt (subtract (VALUE "leadership" entity=(PARAM "Company") timeAggregation="latest") (AVG "leadership" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}trust{{else}}question{{/if}} the leadership's strategy or approach. For Mission and Vision, the {{#if (gt (subtract (VALUE "mission_and_vision" entity=(PARAM "Company") timeAggregation="latest") (AVG "mission_and_vision" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}positive{{else}}negative{{/if}} difference of {{FORMAT (subtract (VALUE "mission_and_vision" entity=(PARAM "Company") timeAggregation="latest") (AVG "mission_and_vision" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="PERCENTAGE"}} suggests employees may be {{#if (gt (subtract (VALUE "mission_and_vision" entity=(PARAM "Company") timeAggregation="latest") (AVG "mission_and_vision" timeAggregation="latest" excludeEntity=(PARAM "Company"))) 0)}}aligned with{{else}}concerned about{{/if}} the leadership's decision-making, communication, or vision.`,
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: '1fa9d17d-9c25-4632-abbd-96b78bdfc826',
            title: 'Leadership',
            position: {
              x: 3,
              y: 264,
              width: 21,
              height: 11,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['leadership'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin: "{{MIN (MIN 'leadership') (MIN 'mission_and_vision')}}",
              yAxisMax: "{{MAX (MAX 'leadership') (MAX 'mission_and_vision')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '293171dc-fa61-4c1e-80ec-01fbbfbb2fc5',
            title: 'Mission and Vision',
            position: {
              x: 24,
              y: 264,
              width: 21,
              height: 11,
            },
            plot: {
              chartType: 'line',
              defaultMetricOptions: ['mission_and_vision'],
              showDiffLabel: true,
              showLegend: true,
              curveSmoothness: 0.1,
              fontSize: 14,
              yAxisMin: "{{MIN (MIN 'leadership') (MIN 'mission_and_vision')}}",
              yAxisMax: "{{MAX (MAX 'leadership') (MAX 'mission_and_vision')}}",
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
            },
          },
          {
            id: '1fc46415-e644-4f5f-8407-56fed589ef22',
            title: '',
            position: {
              x: 0,
              y: 275,
              width: 24,
              height: 10,
            },
            plot: {
              chartType: 'text',
              textHtml:
                'We calculate the growth and compensation score of a company by averaging the scores for current ratings across factors such as senior leadership and CEO, comparing the company with its peers. These scores are then normalized to a 0 to 1 scale. A higher score indicates that employees are generally satisfied with their leadership, while a lower score suggests dissatisfaction.\n\n',
            },
          },
          {
            id: '09cc6ad0-4c79-428d-b5b8-dc9855f4d935',
            title: '',
            position: {
              x: 24,
              y: 275,
              width: 24,
              height: 10,
            },
            plot: {
              chartType: 'gauge',
              colorPalette: ['#FF646D', '#3DC679'],
              defaultMetricOptions: ['leadership'],
              gaugeSubText: 'Leadership',
              gaugePrimaryText:
                '{{FORMAT (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) format="%"}}',
              gaugeValue:
                '{{PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")}}',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: 'c170683d-3631-4aa0-90c6-902072b3d030',
            title: '',
            position: {
              x: 0,
              y: 326,
              width: 48,
              height: 13,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '## Breakdown by location\n\nComparing employee sentiment by metro area provides insight into how workplace experiences vary across different locations, shedding light on which regions foster higher satisfaction and which may be at risk of lower engagement or retention. For example, if a company is expanding operations in a particular metro area, positive employee sentiment in that region can be a strong indicator of success in attracting and retaining talent. Conversely, lower sentiment in certain areas may suggest regional issues such as cost of living, commute times, or local management challenges. Understanding these variations allows companies to tailor their strategies to improve employee satisfaction in specific metro areas and remain competitive in attracting talent.\n\nHere, we show how the overall sentiment ratings of the top 20 metro areas (given at least two employees in each metro area) at {{PARAM "Company"}} compare to those at peer companies.\n\n',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            id: 'fd1b8ad4-7ee4-48ce-bb5c-e628134827a9',
            title:
              'Overall sentiment by metro (top 20): {{PARAM "Company"}} vs comparison group',
            position: {
              x: 0,
              y: 339,
              width: 48,
              height: 18,
            },
            plot: {
              chartType: 'diverging-bar',
              dataSource: 'snowflake',
              defaultMetricOptions: ['overall'],
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: true,
                },
              ],
              showDataLabels: true,
              divergingBar: {
                arrowOrientation: 'vertical',
                positiveLabel: 'Higher Satisfaction',
                negativeLabel: 'Lower Satisfaction',
              },
              comparisonTransformation: {
                baseline: 'second',
              },
              snowflakeConfig: {
                savedQueryId: 'sentiment-msa-breakdown-v1',
                fields: sentimentPeerComparisonSnowflakeFields,
              },
            },
          },
          {
            id: '45dbd56c-522a-4bce-9219-6357cc71fe43',
            title: '',
            position: {
              x: 0,
              y: 362,
              width: 48,
              height: 3,
            },
            plot: {
              chartType: 'text',
              textHtml: '## Review summaries',
            },
          },
          {
            id: 'e0fb2b31-7afe-4f14-a9f9-ccbc8c5df95d',
            title: '',
            position: {
              x: 0,
              y: 365,
              width: 24,
              height: 16,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '### Positive reviews\n\n{{VALUE "positive_review" (PARAM "Company")}}',
            },
          },
          {
            id: '9d721f33-24d6-4d2d-9f71-3f7bed3670e0',
            title: '',
            position: {
              x: 24,
              y: 365,
              width: 24,
              height: 16,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '### Negative reviews\n\n{{VALUE "negative_review" (PARAM "Company")}}',
            },
          },
          {
            id: '61a74496-91aa-4f18-ab21-5e20196cb039',
            title: '',
            position: {
              x: 0,
              y: 398,
              width: 48,
              height: 9,
            },
            plot: {
              chartType: 'text',
              textHtml:
                '## Topics and themes\n\nIn addition to numerical ratings, we also analyze sentiment from the raw text of reviews. Using Natural Language Processing (NLP), we assess employee sentiment on specific topics, such as management and diversity. The resulting sentiment scores are standardized as z-scores, measured relative to a company’s comparison set of source and destination companies based on talent flow. These standardized scores offer valuable insights into the factors that drive employees to join or leave a company.',
            },
          },
          {
            id: '742953c2-a6f7-44c5-b078-ccd12fe98b9d',
            title:
              'Employee sentiment scores for {{PARAM "Company"}} derived from text analysis',
            position: {
              x: 0,
              y: 407,
              width: 48,
              height: 22,
            },
            plot: {
              chartType: 'diverging-bar',
              defaultMetricOptions: ['sentiment_top_bottom'],
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
              showDataLabels: true,
              comparisonTransformation: {
                baseline: 'second',
              },
              divergingBar: {
                negativeLabel: 'Factors of Attrition',
                positiveLabel: 'Factors of Hiring',
              },
            },
          },
          {
            id: '0ac4520f-fa6a-4d5f-81f6-a18bf91e537c',
            title: 'Composition of {{PARAM "Company"}}\'s peer group',
            position: {
              x: 27,
              y: 76,
              width: 21,
              height: 15,
            },
            plot: {
              chartType: 'treemap',
              groupBySegment: false,
              defaultMetricOptions: ['headcount'],
              treemap: { sizeTransform: 'sqrt' },
              format: FormatType.PERCENTAGE,
              plotParameters: [
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
            },
          },
          {
            position: {
              x: 0,
              y: 434,
              width: 48,
              height: 32,
            },
            id: '4e866a2d-860f-4226-8604-13751a030cfd',
            title: '',
            plot: {
              chartType: 'text',
              plotParameters: [
                {
                  parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
                  grouped: false,
                },
                {
                  parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
                  grouped: false,
                },
              ],
              textHtml: `## Recommendations\n\n{{#if (HAS_DATA "overall" entity=(PARAM "Company"))}}\n\n{{#if (lte (PERCENTILE_RANK "overall" entity=(PARAM "Company")) 0.25)}}\n{{PARAM "Company"}} is in the bottom quartile ({{FORMAT (PERCENTILE_RANK "overall" entity=(PARAM "Company")) format="%"}}) compared to its peers regarding overall employee sentiment. This indicates that employees may have widespread concerns or dissatisfaction across various areas, requiring attention to foundational issues.\n{{else if (lte (PERCENTILE_RANK "overall" entity=(PARAM "Company")) 0.5)}}\n{{PARAM "Company"}} is in the second quartile ({{FORMAT (PERCENTILE_RANK "overall" entity=(PARAM "Company")) format="%"}}) compared to its peers regarding overall employee sentiment. While not at the bottom, this position reflects below-average perceptions, suggesting room for growth in fostering a more positive employee experience.\n{{else if (lte (PERCENTILE_RANK "overall" entity=(PARAM "Company")) 0.75)}}\n{{PARAM "Company"}} is in the third quartile ({{FORMAT (PERCENTILE_RANK "overall" entity=(PARAM "Company")) format="%"}}) compared to its peers regarding overall employee sentiment. This indicates above-average performance, with employees generally holding favorable views, though some areas may still need fine-tuning.\n{{else}}\n{{PARAM "Company"}} is in the top quartile ({{FORMAT (PERCENTILE_RANK "overall" entity=(PARAM "Company")) format="%"}}) compared to its peers regarding overall employee sentiment. This reflects strong perceptions of the workplace, with employees likely feeling engaged and supported.\n{{/if}}\n\nA detailed breakdown across the three key areas allows us to evaluate employee sentiment in specific aspects and pinpoint areas for improvement.\n\n### Workplace experience\n\n{{#if (lte (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) 0.25)}}\n{{PARAM "Company"}} is in the bottom quartile ({{FORMAT (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) format="%"}}) compared to its peers, indicating significant room for improvement. To address this, consider conducting anonymous surveys or focus groups to identify concerns, introducing flexible scheduling options, reviewing workplace policies to remove barriers to diversity and inclusion, and providing well-being support like mental health resources and stress management workshops.\n{{else if (lte (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) 0.5)}}\n{{PARAM "Company"}} is in the second quartile ({{FORMAT (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) format="%"}}) compared to its peers, performing below the median. To improve, implement work-life integration programs such as childcare benefits or enhanced PTO policies, provide regular diversity training for managers and employees, establish recognition platforms for employee contributions, and create transparent feedback channels like town halls or suggestion boards.\n{{else if (lte (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) 0.75)}}\n{{PARAM "Company"}} is in the third quartile ({{FORMAT (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) format="%"}}) compared to its peers, performing above the median but with room for further improvement. To strengthen this position, expand access to wellness initiatives like fitness reimbursements, highlight success stories of diversity and inclusion efforts, conduct pulse surveys to refine work-life balance programs, and encourage team-building activities to sustain a positive work environment.\n{{else}}\n{{PARAM "Company"}} is in the top quartile ({{FORMAT (PERCENTILE_RANK "work_life_balance" "diversity_and_inclusion" "culture" entity=(PARAM "Company")) format="%"}}) compared to its peers, reflecting strong performance in this area. To maintain and enhance this success, regularly assess and evolve work-life balance initiatives like sabbaticals or extended parental leave, co-create new diversity strategies with employees, strengthen leadership training aligned with company values, and recognize employees who foster a positive workplace experience.\n{{/if}}\n\n### Growth and compensation\n\n{{#if (lte (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) 0.25)}}\n{{PARAM "Company"}} is in the bottom quartile ({{FORMAT (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) format="%"}}) compared to its peers, indicating significant room for improvement. To address this, conduct employee focus groups to pinpoint specific issues with pay, benefits, and career development opportunities. Introduce transparent salary benchmarking and pay equity audits to rebuild trust in compensation fairness. Launch mentorship programs, offer job-specific training paths, and clearly communicate promotion criteria to improve career growth opportunities.\n{{else if (lte (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) 0.5)}}\n{{PARAM "Company"}} is in the second quartile ({{FORMAT (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) format="%"}}) compared to its peers, performing below the median. Conduct pulse surveys to uncover barriers to fair rewards and professional development. Design career progression maps for all roles, outlining steps and required skills for advancement. Expand training initiatives tailored to employee feedback, and roll out targeted incentives, such as bonuses for achieving development milestones, to drive improvement.\n{{else if (lte (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) 0.75)}}\n{{PARAM "Company"}} is in the third quartile ({{FORMAT (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) format="%"}}) compared to its peers, performing above the median but with room for further improvement. To build on this momentum, use employee surveys to identify specific areas for refinement in career progression and rewards programs. Offer personalized development plans and ensure that leadership regularly communicates opportunities for career advancement. Introduce spot bonuses or non-monetary rewards for outstanding contributions, ensuring recognition aligns with organizational goals.\n{{else}}\n{{PARAM "Company"}} is in the top quartile ({{FORMAT (PERCENTILE_RANK "compensation" "career_growth" "business_outlook" entity=(PARAM "Company")) format="%"}}) compared to its peers, reflecting strong performance in this area. To sustain this advantage, regularly review career development programs to align with evolving employee aspirations. Explore innovative rewards, such as equity grants or flexible benefit packages, to remain competitive. Host annual career planning sessions for employees and provide tools for tracking progress toward personal and professional goals.\n{{/if}}\n\n### Leadership\n\n{{#if (lte (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) 0.25)}}\n{{PARAM "Company"}} is in the bottom quartile ({{FORMAT (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) format="%"}}) compared to its peers, indicating significant room for improvement. To address this, ensure senior leaders and managers communicate clear organizational goals and decision-making processes through regular town halls or Q&A sessions. Implement mandatory leadership training focusing on active listening, communication, and conflict resolution. Establish anonymous feedback mechanisms for employees to voice concerns about leadership practices, and develop action plans to address recurring themes.\n{{else if (lte (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) 0.5)}}\n{{PARAM "Company"}} is in the second quartile ({{FORMAT (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) format="%"}}) compared to its peers, performing below the median. To improve, schedule regular one-on-one check-ins between managers and employees to foster trust and provide clarity on expectations. Roll out coaching sessions for managers on building team cohesion and driving engagement. Introduce a leadership scorecard system to track manager and senior leadership effectiveness based on employee feedback, and provide targeted development opportunities for those needing improvement.\n{{else if (lte (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) 0.75)}}\n{{PARAM "Company"}} is in the third quartile ({{FORMAT (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) format="%"}}) compared to its peers, performing above the median but with room for further improvement. To build on this strength, enhance visibility of senior leadership by hosting regular leadership forums or roundtables. Recognize managers and executives who excel in fostering collaboration and inclusion. Use pulse surveys to identify specific leadership traits valued by employees, and integrate these insights into ongoing leadership development programs.\n{{else}}\n{{PARAM "Company"}} is in the top quartile ({{FORMAT (PERCENTILE_RANK "leadership" "mission_and_vision" entity=(PARAM "Company")) format="%"}}) compared to its peers, reflecting strong performance in this area. To maintain this advantage, implement succession planning initiatives to ensure leadership continuity. Expand opportunities for employees to engage directly with senior leaders through mentorship programs or executive shadowing. Regularly review leadership metrics to identify emerging trends, and celebrate leaders who consistently model the organization's values and drive engagement.\n{{/if}}\n\n{{else}}\nThe selected company has too little data on employee reviews to provide recommendations.\n{{/if}}`,
            },
          },
        ],
        primaryFilter: {
          id: FilterId.COMPANY,
          selectionLimit: 1,
          selectionDefault: 1,
        },
        secondaryFilters: [],
        reportParameters: [
          {
            parameterId: '622c111e-8d5d-426a-9c51-c4053a2b4c8d',
            filterId: FilterId.COMPANY,
            isPrimary: true,
            name: 'Company',
          },
          {
            parameterId: '99a1ccb1-1940-4904-863a-21acb7aa2fb8',
            filterId: FilterId.COMPANY,
            isPrimary: false,
            name: 'Competitors',
            selectionLimit: MAX_REPORT_COMPETITORS,
          },
        ],
      },
    },
  ],
};
